import type { ComponentProps } from 'react';

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@vs-shop/ui/carousel';

import type { VsLinkProps } from '@vs-shop/headless/vsLink';
import Image from '@vs-shop/image-link/Image';
import Link from '@vs-shop/image-link/Link';

import { clsxm, type ClassValue } from '@vs-shop/utils';

import type { FramedMediaProps } from '../../internal/framedMedia.tsx';
import { optionalClassName } from '../../internal/optionalClassName.ts';
import { RenderedPart, type RenderElement } from '../../internal/renderedPart.tsx';

/**
 * A run of photographs a CMS body steps through sideways — a workshop, a range,
 * an event. Flat named parts a tenant arranges in its own JSX, each painting
 * itself.
 *
 *   <ContentGallery>
 *     <Container classNameInner={bodyMeasure}>
 *       <ContentGalleryHeader>
 *         <ContentGalleryTitle render={<Heading level={2} />}>{title}</ContentGalleryTitle>
 *         <ContentGallerySteps>
 *           <ContentGalleryPrevious />
 *           <ContentGalleryNext />
 *         </ContentGallerySteps>
 *       </ContentGalleryHeader>
 *     </Container>
 *
 *     <ContentGalleryTrack>
 *       {photographs.map((photograph) => (
 *         <ContentGalleryItem key={photograph.src}>
 *           <ContentGalleryImage src={photograph.src} alt={photograph.alt} width={573} height={430} />
 *         </ContentGalleryItem>
 *       ))}
 *       <ContentGalleryItem>
 *         <ContentGalleryLink href={destination} target="_blank">
 *           <ContentGalleryImage src={src} alt={alt} width={573} height={430} />
 *         </ContentGalleryLink>
 *       </ContentGalleryItem>
 *     </ContentGalleryTrack>
 *   </ContentGallery>
 *
 * **The header is inside the moving part and the run is not clipped to it.**
 * The primitive's context begins at {@link ContentGallery}, so the two controls
 * can sit beside the title, while whatever the tenant puts between them — its
 * own container, at its own measure — holds the header to the body's width and
 * lets the photographs run to the page's edge.
 *
 * **A photograph that leads somewhere is wrapped, one that does not is not.**
 * {@link ContentGalleryLink} is composed only around a photograph that carries a
 * destination: an anchor without one is an inert box a keyboard still stops on.
 *
 * **The title is not a heading**, for the reason every section title in this
 * tier is not one; a page that wants it in its outline hands the element in
 * through `render`.
 */

/** The gallery: a header over a run of photographs. */
const contentGalleryClasses = 'flex flex-col gap-10';

export type ContentGalleryProps = Omit<ComponentProps<'div'>, 'className'> & {
  readonly className?: ClassValue;
};

/** The gallery. */
export const ContentGallery = ({ className, ...props }: ContentGalleryProps) => (
  <Carousel
    data-slot="content-gallery"
    className={clsxm(contentGalleryClasses, className)}
    {...props}
  />
);

/** The title on one side, the way through the run on the other. */
const contentGalleryHeaderClasses = 'flex items-center justify-between';

export type ContentGalleryHeaderProps = ComponentProps<'div'>;

/** The gallery's header. */
export const ContentGalleryHeader = ({ className, ...props }: ContentGalleryHeaderProps) => (
  <div
    data-slot="content-gallery-header"
    className={clsxm(contentGalleryHeaderClasses, className)}
    {...props}
  />
);

/** What the run is a run of. */
const contentGalleryTitleClasses = 'text-xl text-foreground lap:text-[28px]/9';

export type ContentGalleryTitleProps = ComponentProps<'div'> & {
  /** The element to paint instead of the neutral box — a page's own heading. */
  readonly render?: RenderElement;
};

/** The gallery's title. */
export const ContentGalleryTitle = ({ className, render, ...props }: ContentGalleryTitleProps) => (
  <RenderedPart
    dataSlot="content-gallery-title"
    className={clsxm(contentGalleryTitleClasses, className)}
    render={render}
    {...props}
  />
);

/** The pair of controls, at the header's far end. */
const contentGalleryStepsClasses = 'flex flex-1 justify-end gap-4';

export type ContentGalleryStepsProps = ComponentProps<'div'>;

/** The gallery's controls. */
export const ContentGallerySteps = ({ className, ...props }: ContentGalleryStepsProps) => (
  <div
    data-slot="content-gallery-steps"
    className={clsxm(contentGalleryStepsClasses, className)}
    {...props}
  />
);

export type ContentGalleryStepProps = Omit<
  ComponentProps<typeof CarouselPrevious>,
  'position' | 'navSize'
>;

/**
 * A step back through the run.
 *
 * The primitive's `position="free"` is the gallery's answer rather than a
 * caller's: the controls stand in the header this section drew, not over the
 * photographs.
 *
 * These two name no slot of their own. `@vs-shop/ui/carousel` sets its
 * `carousel-previous` / `carousel-next` AFTER spreading what it is given, so a
 * slot passed here would be dropped silently — the controls are found by the
 * primitive's names, inside {@link ContentGallerySteps}.
 */
export const ContentGalleryPrevious = ({ className, ...props }: ContentGalleryStepProps) => (
  <CarouselPrevious
    navSize="base"
    position="free"
    className={optionalClassName(clsxm(className))}
    {...props}
  />
);

/** A step onward through the run. */
export const ContentGalleryNext = ({ className, ...props }: ContentGalleryStepProps) => (
  <CarouselNext
    navSize="base"
    position="free"
    className={optionalClassName(clsxm(className))}
    {...props}
  />
);

/**
 * The row the photographs travel in.
 *
 * The primitive's own left gutter is removed and a gap put between the places
 * instead: a gutter shows at the row's leading edge, where this run starts at
 * the page's.
 */
const contentGalleryTrackClasses = '-ml-0 flex gap-6';

export type ContentGalleryTrackProps = ComponentProps<typeof CarouselContent>;

/** The gallery's row. */
export const ContentGalleryTrack = ({ className, ...props }: ContentGalleryTrackProps) => (
  <CarouselContent
    data-slot="content-gallery-track"
    className={clsxm(contentGalleryTrackClasses, className)}
    {...props}
  />
);

/** One photograph's place: two abreast on a phone, three on a tablet, four on a laptop. */
const contentGalleryItemClasses = 'basis-1/2 pl-0 tab:basis-1/3 lap:basis-1/4';

export type ContentGalleryItemProps = ComponentProps<typeof CarouselItem>;

/** A photograph's place. */
export const ContentGalleryItem = ({ className, ...props }: ContentGalleryItemProps) => (
  <CarouselItem
    data-slot="content-gallery-item"
    className={clsxm(contentGalleryItemClasses, className)}
    {...props}
  />
);

/** A photograph: a scene, so it fills its place. */
const contentGalleryImageClasses = 'h-full w-full rounded-lg object-cover';

export type ContentGalleryImageProps = FramedMediaProps;

/** A photograph. */
export const ContentGalleryImage = ({ className, ...props }: ContentGalleryImageProps) => (
  <Image
    data-slot="content-gallery-image"
    className={clsxm(contentGalleryImageClasses, className)}
    {...props}
  />
);

/** The click surface of a photograph: the whole of its place. */
const contentGalleryLinkClasses = 'block h-full w-full';

export type ContentGalleryLinkProps = VsLinkProps;

/**
 * A photograph that leads somewhere.
 *
 * Renders through the `Link` seam, so a host that aliases the specifier keeps
 * client-side navigation and one that does not resolves a plain `<a>`.
 *
 * `rel` is supplied for a link that opens elsewhere, and only then: a new tab
 * opened without `rel="noopener noreferrer"` hands the opened page
 * `window.opener` and leaks the referrer, while a same-tab link gains nothing
 * from it. A caller with a reason of its own overrides it natively, and an
 * empty `rel` counts as no reason: it gets the default like an absent one.
 */
export const ContentGalleryLink = ({
  className,
  target,
  rel,
  ...props
}: ContentGalleryLinkProps) => {
  const opensElsewhere = target === '_blank';
  const relProp = rel ? { rel } : opensElsewhere ? { rel: 'noopener noreferrer' } : {};
  const targetProp = target === undefined ? {} : { target };

  return (
    <Link
      data-slot="content-gallery-link"
      className={clsxm(contentGalleryLinkClasses, className)}
      {...targetProp}
      {...relProp}
      {...props}
    />
  );
};
